import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'

import {
  ALL_ACCOMMODATION_SERVICES_QUERY,
  SINGLE_ACCOMMODATION_SERVICE_QUERY,
  CREATE_ACCOMMODATION_SERVICE_MUTATION,
  UPDATE_ACCOMMODATION_SERVICE_MUTATION,
  DELETE_ACCOMMODATION_SERVICE_MUTATION,
  FORCE_DELETE_ACCOMMODATION_SERVICE_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioAccommodationServiceRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_ACCOMMODATION_SERVICE_QUERY, variables).then((data: any) => {
      return hydrateObject(data.accommodationService)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_ACCOMMODATION_SERVICES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.accommodationServices)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_ACCOMMODATION_SERVICE_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.accommodationService),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_ACCOMMODATION_SERVICES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.accommodationServices),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_ACCOMMODATION_SERVICE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.accommodationService)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_ACCOMMODATION_SERVICE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.accommodationService)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_ACCOMMODATION_SERVICE_MUTATION, variables).then((data: any) => {
      return data.deleteRatioAccommodationService
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_ACCOMMODATION_SERVICE_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioAccommodationService
    })
  },
}

export default ratioAccommodationServiceRepository
